/** @format */

import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { track } from "../lib/posthog";
import { api } from "../api/client";
import { Part, StatementOfOperationsOutput } from "../types/sop";
import { formatCycleTime } from "../utils/time";
import {
  extractImageReferences,
  replaceImageReferences,
} from "../utils/imageReferences";

/**
 * Batch-resolve every image:// reference across the PDF payload into a single
 * presigned-URL lookup, then return a resolver suitable for replaceImageReferences.
 * The PDF flow can't reach into useImageHandling's React state, so this runs a
 * standalone fetch and keeps everything in-memory for the duration of the export.
 */
const buildPdfImageResolver = async (
  data: StatementOfOperationsOutput | null,
): Promise<(id: string) => Promise<string | null>> => {
  const refs = new Set<string>();
  const collect = (html?: string | null) => {
    if (!html) return;
    for (const ref of extractImageReferences(html)) {
      refs.add(ref.replace(/^image:\/\//, ""));
    }
  };

  collect(data?.titlePage?.details);
  for (const step of data?.steps ?? []) {
    collect(step.details);
  }

  if (refs.size === 0) {
    return async () => null;
  }

  const requestedIds = Array.from(refs);

  const urlsById: Record<string, string> = {};
  try {
    const res = await api.post("/download_urls", {
      image_ids: requestedIds,
    });
    const fetched: Record<string, string> = res?.data?.urls ?? {};
    for (const [id, url] of Object.entries(fetched)) {
      urlsById[id] = url;
      urlsById[id.toLowerCase()] = url;
    }
  } catch (error) {
    console.error("[PDF Generation] Failed to resolve image URLs:", error);
  }

  return async (id: string) => urlsById[id] ?? urlsById[id.toLowerCase()] ?? null;
};

/**
 * Some step.details payloads come back from the backend wrapped in a full
 * `<html><head></head><body>...</body></html>` document (steps that were
 * touched by the AI regeneration / migration pipeline, for example). When we
 * inline that wrapper inside our outer `<div class="step-details">`, Chromium's
 * HTML5 parser hits a nested-body parse error and re-routes subsequent content
 * — including the `<img>` — to the "after body" insertion mode, which can drop
 * the image from where the CSS expects it. Strip the wrapper so the
 * step-details div contains only the actual editor body.
 */
const stripDocumentWrapper = (html: string): string => {
  if (!html) return html;
  let stripped = html;
  // Strip a full <!DOCTYPE ...> if present.
  stripped = stripped.replace(/^\s*<!doctype[^>]*>/i, "");
  // Strip the opening <html...> / <head>...</head> / <body...> tags and the
  // closing </body></html>. We do this surgically (anchored to start/end) so
  // it can't accidentally swallow inline content that uses a literal `<html>`
  // somewhere in the middle.
  stripped = stripped.replace(/^\s*<html\b[^>]*>\s*/i, "");
  stripped = stripped.replace(/<\/html>\s*$/i, "");
  stripped = stripped.replace(/^\s*<head\b[^>]*>[\s\S]*?<\/head>\s*/i, "");
  stripped = stripped.replace(/^\s*<body\b[^>]*>\s*/i, "");
  stripped = stripped.replace(/<\/body>\s*$/i, "");
  return stripped;
};

// Function to parse and clean title page content
const parseTitlePageContent = (htmlContent: string): string => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  const avatarImages = tempDiv.querySelectorAll(
    'img[isavatar="true"], img[data-avatar="true"]',
  );

  avatarImages.forEach((img) => {
    const src = img.getAttribute("src") || "";

    if (
      src.includes("/api/placeholder/") ||
      src.includes("placeholder") ||
      src.includes("fallback") ||
      src === "/api/placeholder/32/32"
    ) {
      console.log("[PDF Generation] Removing placeholder avatar:", src);
      img.remove();
    }
  });

  return tempDiv.innerHTML;
};

const splitTitlePageAroundCoverImage = (
  htmlContent: string,
): { before: string; cover: string | null; after: string } => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;

  const images = Array.from(tempDiv.querySelectorAll("img"));
  const coverImg = images.find(
    (img) => !img.hasAttribute("data-avatar") && !img.closest("[data-column]"),
  );

  // When the user hasn't uploaded a cover yet, we store a Tiptap image placeholder node
  // as <div data-image-placeholder="true" data-placeholder-id="cover-placeholder-...">.
  const coverPlaceholder = tempDiv.querySelector(
    'div[data-image-placeholder="true"][data-placeholder-id^="cover-placeholder-"]',
  );

  if (!coverImg && !coverPlaceholder) {
    return { before: tempDiv.innerHTML, cover: null, after: "" };
  }

  const coverElement = coverImg ?? coverPlaceholder;
  if (!coverElement) return { before: tempDiv.innerHTML, cover: null, after: "" };

  const coverContainer =
    coverElement.closest("figure") ||
    coverElement.closest("p") ||
    coverElement.closest("div") ||
    coverElement.parentElement;

  if (!coverContainer)
    return { before: tempDiv.innerHTML, cover: null, after: "" };

  const start = document.createComment("COVER_START");
  const end = document.createComment("COVER_END");
  coverContainer.before(start);
  coverContainer.after(end);

  const serialized = tempDiv.innerHTML;
  const startToken = "<!--COVER_START-->";
  const endToken = "<!--COVER_END-->";

  const startIdx = serialized.indexOf(startToken);
  const endIdx = serialized.indexOf(endToken);

  if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) {
    return { before: tempDiv.innerHTML, cover: null, after: "" };
  }

  const before = serialized.slice(0, startIdx);
  const cover = serialized.slice(startIdx + startToken.length, endIdx);
  const after = serialized.slice(endIdx + endToken.length);

  return { before, cover, after };
};

const style = `
  <style>
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      margin: 0;
      padding: 20px;
      background: white;
    }
    
    /* Title page styles */
    .title-page {
      text-align: center;
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      min-height: calc(100vh - 95px);
    }
    
    .title-page h1 {
      font-size: 28px;
      font-weight: bold;
      color: #3b82f6;
      margin-bottom: 20px;
    }
    
    .title-page .subtitle {
      font-size: 16px;
      font-style: italic;
      color: #94a3b8;
      margin-bottom: 40px;
    }
    
    .title-page .cover-image {
      margin: 20px 0;
      text-align: center;
    }
    
    .title-page .cover-image img {
      max-width: 400px;
      height: auto;
    }
    
    /* Authored content styles */
    .authored-content {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 2rem;
      margin-top: 40px;
    }
    
    .authored-column {
      text-align: center;
    }
    
    .authored-column .label {
      font-weight: bold;
      font-size: 14px;
      margin-bottom: 10px;
    }
    
    .authored-column .avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      margin: 5px auto;
      display: block;
    }
    
    .authored-column .name {
      font-size: 12px;
      margin: 5px 0;
    }
    
    .authored-column .date {
      font-size: 10px;
      font-style: italic;
    }
    
    /* Step content styles */
    .step-content {
      margin-bottom: 40px;
      page-break-inside: avoid;
    }
    
    .step-title {
      font-size: 20px;
      font-weight: bold;
      color: #2563eb;
      margin-bottom: 15px;
    }
    
    .step-details {
      margin-bottom: 20px;
    }

    /* Constrain any embedded image to the available page width while preserving
     * aspect ratio. The editor stores explicit width/height attributes (often
     * larger than the printed page minus margins), so we override them here. */
    .step-content img,
    .step-details img,
    .title-page img {
      max-width: 100%;
      height: auto;
      display: inline-block;
    }

    .section-title {
      font-size: 16px;
      font-weight: bold;
      margin: 24px 0 8px 0;
    }
    
    /* Table styles */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    
    th, td {
      border: 1px solid #e2e8f0;
      padding: 8px 12px;
      text-align: left;
    }
    
    th {
      background-color: #f8fafc;
      font-weight: bold;
    }

    /* Task list (checkbox) items: keep checkbox and text on the same line in PDF */
    ul[data-type="taskList"] {
      list-style: none;
      margin: 0.5em 0;
      padding: 0;
      margin-left: 0;
    }
    ul[data-type="taskList"] li {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      margin: 0.25em 0;
      list-style: none;
    }
    ul[data-type="taskList"] li > label {
      flex: 0 0 auto;
      margin: 0;
      user-select: none;
    }
    ul[data-type="taskList"] li > div,
    ul[data-type="taskList"] li > p {
      flex: 1 1 auto;
      display: inline;
      margin: 0;
    }
    ul[data-type="taskList"] li > div p {
      display: inline;
      margin: 0;
    }

    .pdf-hidden {
      display: none;
    }
  </style>
`;

// Function to generate complete HTML content for PDF
export const generateHtmlForPdf = async (
  data: StatementOfOperationsOutput | null,
): Promise<string> => {
  const resolveImageUrl = await buildPdfImageResolver(data);
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${data?.sopTitle || "SOP Document"}</title>
      ${style}
    </head>
    <body>
  `;

  if (
    data?.titlePage &&
    (data.titlePage.details ||
      (data.titlePage.materials && data.titlePage.materials.length > 0) ||
      (data.titlePage.revisions && data.titlePage.revisions.length > 0))
  ) {
    console.log("[PDF Generation] Adding title page HTML");
    const titlePageWithUrls = data.titlePage.details
      ? stripDocumentWrapper(
          await replaceImageReferences(data.titlePage.details, resolveImageUrl),
        )
      : "";
    const contentParsed = titlePageWithUrls
      ? parseTitlePageContent(titlePageWithUrls)
      : "";
    const { before, cover, after } = contentParsed
      ? splitTitlePageAroundCoverImage(contentParsed)
      : { before: "", cover: null, after: "" };
    html += `
      <div class="title-page">
        ${cover ? `${before}${cover}` : contentParsed}
    `;
    if (data.titlePage.revisions && data.titlePage.revisions.length > 0) {
      const fmtDate = (iso: string) => {
        try {
          // If it's just a date (YYYY-MM-DD), don't convert through timezone
          if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
            const [year, month, day] = iso.split("-").map(Number);
            return new Date(year, month - 1, day).toLocaleDateString(
              undefined,
              { dateStyle: "short" },
            );
          }
          // Otherwise, parse as ISO datetime
          const d = new Date(iso);
          return Number.isNaN(d.getTime())
            ? iso
            : d.toLocaleDateString(undefined, { dateStyle: "short" });
        } catch {
          return iso;
        }
      };
      html += `
        <h3 class="section-title" style="margin-top: 24px;">Revision history</h3>
        <table>
          <thead>
            <tr>
              <th>Rev</th>
              <th>Description</th>
              <th>Created at</th>
              <th>Created by</th>
            </tr>
          </thead>
          <tbody>
      `;
      data.titlePage.revisions.forEach(
        (r: {
          revision: number;
          description: string;
          created_at: string;
          created_by: string;
        }) => {
          const safeDesc = String(r.description)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
          const safeBy = String(r.created_by)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
          html += `<tr><td>${r.revision}</td><td>${safeDesc}</td><td>${fmtDate(r.created_at)}</td><td>${safeBy}</td></tr>`;
        },
      );
      html += `
          </tbody>
        </table>
      `;
    }
    if (data.titlePage.materials && data.titlePage.materials.length > 0) {
      html += `
        <h3 class="section-title" style="margin-top: 24px;">Materials</h3>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
      `;
      data.titlePage.materials.forEach(
        (m: { name: string; quantity: number }) => {
          const safeName = String(m.name)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
          html += `<tr><td>${safeName}</td><td>${m.quantity}</td></tr>`;
        },
      );
      html += `
          </tbody>
        </table>
      `;
    }
    if (cover) {
      html += `
        ${after}
      `;
    }
    html += `
      </div>
    `;
  }

  if (data?.steps && data.steps.length > 0) {
    for (const [index, step] of data.steps.entries()) {
      console.log(`[PDF Generation] Adding step ${index + 1}: ${step.title}`);

      const cycleTimeStr = formatCycleTime(step.cycleTime);
      const stepTitleSuffix = cycleTimeStr ? ` ${cycleTimeStr}` : "";
      const stepDetailsBeforeResolve = step.details;
      const stepDetailsResolved = stepDetailsBeforeResolve
        ? await replaceImageReferences(stepDetailsBeforeResolve, resolveImageUrl)
        : "";
      const stepDetailsWithUrls = stripDocumentWrapper(stepDetailsResolved);
      // Diagnostic: log each step's before/after so we can see exactly which
      // <img> tags survive into the PDF payload and what their src looks like.
      // Cropped/annotated tags WITHOUT data-image-id matching anything in
      // /download_urls will show their original (pre-resolve) src here — that's
      // the failure mode we're hunting.
      console.log(`[PDF Generation] Step ${index + 1} details after resolve:`, stepDetailsWithUrls);
      html += `
        <div class="step-content">
          <h2 class="step-title">Step ${index + 1}: ${step.title}${stepTitleSuffix}</h2>

          <div class="step-details">
            ${stepDetailsWithUrls}
          </div>
      `;

      if (step.parts && step.parts.length > 0) {
        console.log("[PDF Generation] Adding parts table");
        html += `
          <h3 class="section-title">Parts</h3>
          <table>
            <thead>
              <tr>
                <th>Name</th>
        `;

        const hasQuantity = step.parts.some(
          (part: Part) =>
            part.quantity !== undefined &&
            part.quantity !== null &&
            part.quantity !== 0,
        );
        if (hasQuantity) {
          html += `<th>Quantity</th>`;
        }

        html += `
              </tr>
            </thead>
            <tbody>
        `;

        step.parts.forEach((part: Part) => {
          html += `
            <tr>
              <td>${part.name}</td>
          `;
          if (hasQuantity) {
            html += `<td>${part.quantity || ""}</td>`;
          }
          html += `</tr>`;
        });

        console.log(
          `[PDF Generation] Added ${step.parts.length} parts to step ${index + 1}`,
        );

        html += `
            </tbody>
          </table>
        `;
      }

      // if (step.tools.length) {
      //   console.log(
      //     `[PDF Generation] Adding tools table for step ${index + 1}`,
      //   );
      //   html += `
      //     <h3 class="section-title">Tools</h3>
      //     <table>
      //       <thead>
      //         <tr>
      //           <th>Name</th>
      //   `;

      //   const hasQuantity = step.tools.some(
      //     (tool: Tool) =>
      //       tool.quantity !== undefined &&
      //       tool.quantity !== null &&
      //       tool.quantity !== 0,
      //   );
      //   const hasMpim = step.tools.some(
      //     (tool: Tool) =>
      //       tool.mpim !== undefined && tool.mpim !== null && tool.mpim !== "",
      //   );
      //   const hasValue = step.tools.some(
      //     (tool: Tool) =>
      //       tool.value !== undefined && tool.value !== null && tool.value !== 0,
      //   );
      //   const hasCost = step.tools.some(
      //     (tool: Tool) =>
      //       tool.cost !== undefined && tool.cost !== null && tool.cost !== 0,
      //   );

      //   if (hasQuantity) html += `<th>Quantity</th>`;
      //   if (hasMpim) html += `<th>MPIM</th>`;
      //   if (hasValue) html += `<th>Value</th>`;
      //   if (hasCost) html += `<th>Cost</th>`;

      //   html += `
      //         </tr>
      //       </thead>
      //       <tbody>
      //   `;

      //   step.tools.forEach((tool: Tool) => {
      //     html += `
      //       <tr>
      //         <td>${tool.name}</td>
      //     `;
      //     if (hasQuantity) html += `<td>${tool.quantity || ""}</td>`;
      //     if (hasMpim) html += `<td>${tool.mpim || ""}</td>`;
      //     if (hasValue) html += `<td>${tool.value || ""}</td>`;
      //     if (hasCost) html += `<td>${tool.cost || ""}</td>`;
      //     html += `</tr>`;
      //   });

      //   console.log(
      //     `[PDF Generation] Added ${step.tools.length} tools to step ${index + 1}`,
      //   );

      //   html += `
      //       </tbody>
      //     </table>
      //   `;
      // }

      html += `</div>`;
    }
  }

  html += `
    </body>
    </html>
  `;

  // Strip lazy/deferred-loading attributes before the HTML reaches Playwright.
  // Tiptap saves every <img> with loading="lazy" decoding="async" because those
  // are good for browser performance. In PDF generation, Playwright calls
  // set_content(html, wait_until='load'): the load event fires after eager
  // resources are ready but explicitly DEFERS lazy images until they scroll
  // into the 1200×800 viewport — so steps 3+ never paint (they're below the
  // fold). Steps 1 & 2 (raw AI-generated HTML) lack those attributes and
  // render fine, which explains the exact symptom the user reported.
  html = html
    .replace(/\sloading="lazy"/gi, "")
    .replace(/\sdecoding="async"/gi, "");

  console.log("[PDF Generation] ✅ Completed generating HTML for PDF");

  return html;
};

type DownloadErrorHandler = (message: string) => void;
type ApiErrorPayload = { message?: string; error?: string };
type ApiErrorResponse = { status?: number; data?: ApiErrorPayload };
type ApiLikeError = { message?: string; response?: ApiErrorResponse };

const getDownloadErrorMessage = (fallbackMessage: string, error: unknown) => {
  if (!error || typeof error !== "object") {
    return fallbackMessage;
  }

  const apiLikeError = error as ApiLikeError;
  const statusCode = apiLikeError.response?.status;
  const apiMessage =
    apiLikeError.response?.data?.message ??
    apiLikeError.response?.data?.error ??
    apiLikeError.message;

  if (apiMessage && statusCode) {
    return `${fallbackMessage} (HTTP ${statusCode}: ${apiMessage})`;
  }

  if (apiMessage) {
    return `${fallbackMessage} (${apiMessage})`;
  }

  return fallbackMessage;
};

const usePdfGenerate = (
  analyzedOutput: StatementOfOperationsOutput | null,
  onError?: DownloadErrorHandler,
) => {
  const [downloading, setDownloading] = useState(false);
  const [sopBlobUrl, setSopBlobUrl] = useState<string | null>(null);
  const [prevOutput, setPrevOutput] =
    useState<StatementOfOperationsOutput | null>(null);

  const downloadPdf = async () => {
    setDownloading(true);

    if (sopBlobUrl && prevOutput === analyzedOutput) {
      window.open(sopBlobUrl);
      setDownloading(false);
      return;
    }

    setPrevOutput(analyzedOutput);

    try {
      const htmlContent = await generateHtmlForPdf(analyzedOutput);

      const sopTitle = analyzedOutput?.sopTitle?.trim() || "SOP Document";
      const body: {
        html: string;
        sopId: string;
        sopTitle: string;
        title: string;
        version_id?: string;
      } = {
        html: htmlContent,
        sopId: analyzedOutput?.sopId || "",
        sopTitle,
        title: sopTitle,
      };
      if (analyzedOutput?.versionId) body.version_id = analyzedOutput.versionId;
      const response = await api.post("/generate-pdf", body, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });

      URL.revokeObjectURL(sopBlobUrl!); // Revoke the old URL
      const url = URL.createObjectURL(blob);
      setSopBlobUrl(url);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${sopTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a); // Cleanup
      try {
        mixpanel.track("Downloaded SOP PDF");
        track("sop_downloaded", { format: "pdf" });
      } catch {
        // silent catch -- do nothing
      }
    } catch (error) {
      console.error("PDF generation failed:", error);
      onError?.(
        getDownloadErrorMessage(
          "Failed to download PDF. Please try again.",
          error,
        ),
      );
    } finally {
      setDownloading(false);
    }
  };

  return { downloading, downloadPdf };
};

export default usePdfGenerate;
